// Native orderBy implementation to replace lodash
export function orderBy<T>(
  array: T[],
  keys: string | string[],
  orders?: "asc" | "desc" | ("asc" | "desc")[]
): T[] {
  const keysArray = Array.isArray(keys) ? keys : [keys];
  const ordersArray = Array.isArray(orders) ? orders : [orders || "asc"];

  return [...array].sort((a, b) => {
    for (let i = 0; i < keysArray.length; i++) {
      const key = keysArray[i];
      const order = ordersArray[i] || "asc";
      const aVal = (a as Record<string, unknown>)[key] as string | number | Date;
      const bVal = (b as Record<string, unknown>)[key] as string | number | Date;

      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
    }
    return 0;
  });
}

export interface IStatus {
  status: boolean;
  message: string;
}

export function setCookie(name: string, value: string, days: number) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

export function getCookie(name: string) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

export function setLocalStorage(
  name: string,
  value: unknown,
  stringify: boolean = true
) {
  name = "designingwithml_" + name;
  if (stringify) {
    localStorage.setItem(name, JSON.stringify(value));
  } else {
    localStorage.setItem(name, String(value));
  }
}

export const downloadBase64File = (base64Data: string, fileName: string) => {
  const linkSource = base64Data;
  const downloadLink = document.createElement("a");
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
  downloadLink.remove();
};

export function getLocalStorage(name: string, stringify: boolean = true): unknown {
  name = "designingwithml_" + name;
  if (typeof window !== "undefined") {
    const value = localStorage.getItem(name);
    try {
      if (stringify) {
        return JSON.parse(value!);
      } else {
        return value;
      }
    } catch {
      return null;
    }
  } else {
    return null;
  }
}

export function fetchJSON(
  url: string | URL,
  payload: RequestInit = {},
  onSuccess: (data: unknown) => void,
  onError: (error: IStatus) => void
) {
  return fetch(url, payload)
    .then(function (response) {
      if (response.status !== 200) {
        onError({
          status: false,
          message:
            "Connection error " + response.status + " " + response.statusText,
        });
        return;
      }
      return response.json().then(function (data) {
        onSuccess(data);
      });
    })
    .catch(function (err) {
      onError({ status: false, message: "Connection error " + err });
    });
}

export function fetchBlob(
  url: string | URL,
  payload: RequestInit = {},
  onSuccess: (data: Blob) => void,
  onError: (error: IStatus) => void
) {
  return fetch(url, payload)
    .then(function (response) {
      if (response.status !== 200) {
        onError({
          status: false,
          message:
            "Connection error " + response.status + " " + response.statusText,
        });
        return;
      }
      return response.blob().then(function (data) {
        onSuccess(data);
      });
    })
    .catch(function (err) {
      onError({ status: false, message: "Connection error " + err });
    });
}

export function eraseCookie(name: string) {
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
}

export function truncateText(text: string | null | undefined, length = 50) {
  if (!text) {
    return "";
  }
  if (text.length > length) {
    return text.substring(0, length) + " ...";
  }
  return text;
}

export function rebalanceWeights(
  weights: number[],
  index: number,
  value: number
) {
  const num_items = weights.length - 1;
  const budget = 1 - value;
  const other_value = Math.round((budget / num_items) * 100) / 100;
  const new_weights = weights.map((_, i) => {
    if (i == index) {
      return value;
    } else {
      return other_value;
    }
  });
  return new_weights;
}

// Reduce markdown to plain text so previews can be truncated at any point
// without leaving broken syntax (e.g. a link cut mid-URL rendering literally)
export const stripMarkdown = (markdown: string): string => {
  return markdown
    .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // images -> alt text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links -> link text
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // bold
    .replace(/(\*|_)(?=\S)(.*?)(?<=\S)\1/g, "$2") // italics
    .replace(/`([^`]*)`/g, "$1") // inline code
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/^\s*[-*+]\s+/gm, "") // bullet markers
    .replace(/\n+/g, " ") // newlines -> spaces
    .replace(/\s{2,}/g, " ")
    .trim();
};

export const truncateDescription = (
  description: string,
  maxLength: number = 100
): string => {
  const plain = stripMarkdown(description);
  if (plain.length <= maxLength) return plain;
  return plain.slice(0, maxLength) + "...";
};

export const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return new Date(dateString)
    .toLocaleDateString("en-US", options)
    .replace(/(\w+)\s(\d+),\s(\d+)/, "$1 $2, $3");
};

// Utility function to combine class names
export function cn(
  ...classes: (string | string[] | undefined | null | false)[]
): string {
  return classes.flat().filter(Boolean).join(" ");
}
